import React, { useState } from 'react';
import {
  Mail, Phone, MapPin, Send, Building2,
  Clock, CheckCircle, AlertCircle, Loader2,
  MessageSquare, User, Briefcase
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';

const ContactPage = () => {
  usePageTitle('Contact - SYLink Pentest');
  const { t } = useI18n();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const subjects = [
    { value: '', label: 'Select a subject' },
    { value: 'demo', label: 'Demo request' },
    { value: 'pricing', label: 'Question sur les tarifs' },
    { value: 'support', label: 'Support technique' },
    { value: 'partnership', label: 'Partenariat' },
    { value: 'other', label: 'Autre' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/v1/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Your message has been sent successfully. We will get back to you as soon as possible.'
        });
        setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      } else {
        throw new Error(data.detail || 'Une erreur est survenue');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to send the message. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-green/10 via-transparent to-blue-500/10" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-brand-green/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/10 border border-brand-green/30 rounded-full text-brand-green text-sm mb-6">
              <MessageSquare className="w-4 h-4" />
              We are here to help
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contactez-<span className="text-brand-green">nous</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              A question, a project, or need for information? Our team is here to support you.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Cards */}
            <div className="bg-brand-darker border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-brand-green/10 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Phone</h3>
                  <a href="tel:+33415540000" className="text-gray-400 hover:text-brand-green transition-colors">
                    +33 4 15 54 00 00
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-brand-darker border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Email</h3>
                  <a href="mailto:contact@sylink.fr" className="text-gray-400 hover:text-blue-400 transition-colors">
                    contact@sylink.fr
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-brand-darker border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Adresse</h3>
                  <p className="text-gray-400 text-sm">
                    35 rue Blatin, 63000 Clermont-Ferrand
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-brand-darker border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Horaires</h3>
                  <p className="text-gray-400 text-sm">
                    Lun - Ven : 9h00 - 18h00
                  </p>
                </div>
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-gradient-to-br from-brand-green/10 to-blue-500/10 border border-brand-green/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-6 h-6 text-brand-green" />
                <h3 className="text-white font-semibold">SYLink</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                French automated pentest solution powered by artificial intelligence.
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-brand-green" />
                <span>100% Sovereign - Hosted in France</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-brand-darker border border-gray-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Envoyez-nous un message</h2>

              {/* Status Messages */}
              {status.message && (
                <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                  status.type === 'success'
                    ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                    : 'bg-red-500/10 border border-red-500/30 text-red-400'
                }`}>
                  {status.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span>{status.message}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom complet *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Jean Dupont"
                        className="w-full pl-10 pr-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={t('auth.emailPlaceholder')}
                        className="w-full pl-10 pr-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Entreprise
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Nom de votre entreprise"
                        className="w-full pl-10 pr-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green transition-colors"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Sujet *
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors appearance-none cursor-pointer"
                      >
                        {subjects.map(subject => (
                          <option key={subject.value} value={subject.value} className="bg-brand-darker">
                            {subject.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Describe your request in detail..."
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green transition-colors resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto px-8 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Envoyer le message
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500">
                  * Required fields. Your data is processed in accordance with our privacy policy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
